// ============================================================
// limitationRules.ts — the amber checks on a LIMITATION deadline's date
// (pure; limitationRules.test.ts). WARNINGS ONLY: they never block a save and
// never change a date. Used by the case's Deadlines section (list + add/edit)
// and the case quick view.
//   • due date before the accident date → BLOCKED by the server
//     (backend lib/limitationDates.ts); shown here as an error too.
//   • client under 18 on the accident date and the limitation date is before
//     their 21st birthday (3 years after their 18th) → amber.
//   • an adult and the date isn't accident date + 3 years → amber (it may be
//     right, e.g. a later date of knowledge).
// Dates: "YYYY-MM-DD" (or the free-text formats — textDateToIso).
// ============================================================
import { addYearsIso, textDateToIso, ukDate, yearsBetween } from './civilDate'

export const isLimitationName = (name: string | null | undefined): boolean => /limitation/i.test(name ?? '')

// ── THE estimated limitation date (when no "Limitation date" deadline is
// saved). Mirrored by backend/src/lib/limitationEstimate.ts — keep in step
// (backend/test/audit-limitation-parity.test.ts).
//   a. a date the user set themselves (limitationOverrideDate) → that date
//      ('override');
//   b. base: client under 18 on the accident date → 3 years after their 18th
//      birthday ('child'); an adult → accident + 3 years ('adult'); no
//      readable date of birth → accident + 3 years, flagged ('noDob');
//   c. CICA → incident + 2 years ('cica'; a child gets a note);
//   d. HOLIDAY_FOREIGN → accident + 2 years ('holiday', with a note);
//   e. FATAL with a date of death before the base date → death + 3 years
//      ('fatal'); death on/after it → the base date + a note;
//   f. a date of knowledge LATER than the accident never moves it — a note
//      gives the date it would give;
//   g. a protected party (PiMatter.protectedParty) — the same date, plus a
//      note that time may not be running. A fatal claim with no date of
//      death gets a note asking for it.
//   h. EL_DISEASE / CLINICAL_NEGLIGENCE — the same date, plus a note that
//      they usually run from the date of knowledge.
// DB batch 2 (B2b, research 28 Oct):
//   • HOLIDAY_FOREIGN by regime — none → accident + 1y (a note asks for it);
//     PACKAGE → the usual 3y (child rule); AIR / SEA → arrival (else the
//     accident) + 2y, no child extension; FOREIGN_LAW → accident + the months
//     entered (else 1y, noted), no child extension.
//   • route MIB_UNTRACED (road traffic only — audit fix 1) → accident + 3y, no
//     child extension.
//   • CICA → adult: incident + 2y; a child: reported to police before their
//     18th birthday → the day before their 20th; on/after → report + 2y; no
//     report date → incident + 2y, asking for it. Not the general child rule.
//   • ABUSE, sexual, a child, CONFIRMED → no estimate (no time limit, s.11ZA
//     — noTimeLimitApplies); otherwise the usual rule + a note.
//   • MILITARY + overseas operation → never later than the longstop (the
//     accident, or the date of knowledge if later, + 6y), not even an own date.
//   • EL_DISEASE → the EARLIEST of the accident, first symptoms, diagnosis
//     and knowledge + 3y, noted (audit fix 1: not only when there's no
//     accident date); none → no estimate. CLINICAL_NEGLIGENCE: the disease
//     dates can only make it earlier.
//   • Audit fix 1: two dates of birth on file → the earlier estimate; a fact
//     that moves it LATER than the type's standard date says "Without it: …".
// CONSERVATIVE on purpose: the EARLIEST plausible date. Notes = internal
// guidance shown in the case's limitation box (never client-facing).
// A protected party keeps the ordinary (earlier) date — only a note (g).
// dobRaw = THE case DOB (utils/caseDob). A DOB after the accident (bad data,
// warned elsewhere) counts as an adult. 29 Feb: addYearsIso lands on 28 Feb in
// a non-leap year — the EARLIER day, so the estimate never runs late.
export type LimitationBasis =
  | 'adult' | 'child' | 'noDob' | 'override' | 'cica' | 'holiday' | 'fatal'
  // DB batch 2 (B2b)
  | 'holidayNoRegime' | 'travel' | 'foreignLaw' | 'mib' | 'cicaChild' | 'cicaReport' | 'disease' | 'militaryCap'
// The B2 claim facts on the case (all optional; dates "YYYY-MM-DD" or the
// free-text formats textDateToIso reads).
export interface LimitationClaimFacts {
  claimType?: string | null
  claimRoute?: string | null
  dateOfKnowledge?: string | null
  dateOfDeath?: string | null
  overrideDate?: string | null
  // PiMatter.protectedParty (CPR 21 — may lack capacity). Never moves the date.
  protectedParty?: boolean | null
  // DB batch 2 (B2b)
  holidayRegime?: string | null
  travelArrivalDate?: string | null
  foreignLimitationMonths?: number | null
  abuseType?: string | null
  abuseNoLimitConfirmedAt?: string | null
  policeFirstReportedAt?: string | null
  overseasOperation?: boolean | null
  firstSymptomsDate?: string | null
  diagnosisDate?: string | null
}
export interface LimitationEstimate { ymd: string; basis: LimitationBasis; notes: string[] }
// (Audit fix 1, H3) The date of birth: one value, or THE case DOB plus the
// OTHER readable one when the case copy and the client record differ
// (caseDob otherDob). With two, the one giving the EARLIER estimate is used.
export type DobInput = string | null | undefined | { dob: string | null; otherDob?: string | null }
// Internal guidance shown beside the estimate (never client-facing).
export const LIMITATION_NOTES = {
  cicaChild: 'CICA rules for children differ. Check and set the date yourself.',
  holiday: 'Air and sea accident claims can be 2 years and foreign law may differ. Check and set the date yourself.',
  deathAfterExpiry: 'Death after limitation expired. Check.',
  dateOfKnowledge: (ymd: string): string => `Date of knowledge would give ${ukDate(ymd)}. If you rely on it, set the date yourself with a reason.`,
  fatalNoDeath: 'Add the date of death to check the limitation date.',
  fatalLater: (baseYmd: string): string => `Based on the date of death. Without it: ${ukDate(baseYmd)}.`,
  capacity: 'Client may lack capacity, so time may not be running. Check and set the date yourself.',
  knowledgeClaim: 'Disease and clinical negligence claims usually run from the date of knowledge. Check it and set an own date if needed.',
  // DB batch 2 (B2b)
  holidayNoRegime: 'Choose the type of holiday claim (package / air / sea / foreign law) for an accurate date.',
  holidayPackage: 'Package holiday claims: the usual 3 years from the accident (the child rule applies).',
  holidayPackageTravel: 'If the injury happened on the flight or ship, choose Air or Sea (2 years).',
  holidayTravel: 'Air and sea claims: 2 years from arrival (or disembarkation), even for children.',
  holidayNoArrival: 'Add the arrival (or disembarkation) date for an accurate date. The accident date is used until then.',
  foreignLaw: (months: number): string => `Foreign law: ${months} month${months === 1 ? '' : 's'} from the accident, as entered. No child extension is applied. Check the foreign rule.`,
  foreignLawNoMonths: 'Add the foreign limitation period (in months) for an accurate date. 1 year is used until then.',
  mib: 'Untraced driver (MIB) claims: 3 years from the accident, even for children.',
  cicaNoReport: 'Add the date it was first reported to police for an accurate CICA date.',
  cicaChildBefore18: 'Reported to police before their 18th birthday: the CICA application must be in before their 20th birthday.',
  cicaChildFrom18: 'Reported to police on or after their 18th birthday: 2 years from the first report.',
  abuseMaybeNoLimit: 'Child sexual abuse claims may have no time limit (s.11ZA, from 29 June 2026). Confirm in the Claim card if it applies.',
  abuseConfirmStale: 'Confirmation no longer applies. Check and confirm again.',
  militaryCap: (ymd: string, from: string): string => `Overseas operations longstop: ${ukDate(ymd)} (${from} + 6 years).`,
  ownDateOverCap: 'The own date is later than the overseas operations longstop, so the longstop is used.',
  diseaseBase: (which: string): string => `Based on the ${which}. Disease claims usually run from the date of knowledge. Check it.`,
  // (Audit fix 1)
  twoDobs: (a: string, b: string): string => `Two dates of birth on file (${ukDate(a)} / ${ukDate(b)}). Using the one that gives the earlier date. Fix the wrong one.`,
  diseaseBeforeAccident: (which: string): string => `Based on the ${which}, which is before the accident date. Check both dates.`,
  // A rule that gives a LATER date than the type's standard one: what it
  // would be without the fact that moved it (a mistyped date must never
  // quietly add time).
  withoutIt: (what: string, standardYmd: string): string => `Based on the ${what}. Without it: ${ukDate(standardYmd)}.`,
  diedNotFatal: 'Client has died. Consider changing the claim type to Fatal (limitation can run from the date of death).',
  reportBeforeIncident: 'The date first reported to police is before the incident, so it isn’t used. Check it.',
  arrivalBeforeAccident: 'The arrival date is before the accident date, so it isn’t used. Check it.',
}
// "No time limit" wording (child sexual abuse, confirmed).
export const NO_TIME_LIMIT = 'No time limit (Limitation Act s.11ZA).'
export const NO_TIME_LIMIT_CAVEAT = 'The defendant can still argue a fair trial isn’t possible.'

// Civil-date steps the rules need (clamped to the month's last day — the
// EARLIER side, like addYearsIso).
const addMonthsIso = (iso: string, months: number): string | null => {
  const y0 = Number(iso.slice(0, 4)); const m0 = Number(iso.slice(5, 7)); const d0 = Number(iso.slice(8, 10))
  if (!y0 || !m0 || !d0) return null
  const total = y0 * 12 + (m0 - 1) + months
  const y = Math.floor(total / 12); const mo = (total % 12) + 1
  const dim = new Date(Date.UTC(y, mo, 0)).getUTCDate()
  return `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(Math.min(d0, dim)).padStart(2, '0')}`
}
const addDaysIso = (iso: string, days: number): string => new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10)
const validMonths = (n: unknown): number | null => (typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 120 ? n : null)
// Under 18 on the day (a DOB after the day — bad data — counts as an adult).
const childOn = (dob: string | null, day: string): boolean => !!dob && dob <= day && yearsBetween(dob, day) < 18
// The date in an estimate's "Without it: DD/MM/YYYY" note (none → the far future).
const withoutYmd = (e: LimitationEstimate): string => {
  for (const n of e.notes) {
    const m = /Without it: (\d{2})\/(\d{2})\/(\d{4})\./.exec(n)
    if (m) return `${m[3]}-${m[2]}-${m[1]}`
  }
  return '9999-12-31'
}
// The readable date(s) of birth (a second one only when it differs).
const dobPair = (d: DobInput): { primary: string | null; other: string | null } => {
  if (d && typeof d === 'object') {
    const primary = textDateToIso(d.dob ?? null)
    const other = textDateToIso(d.otherDob ?? null)
    return primary ? { primary, other: other && other !== primary ? other : null } : { primary: other, other: null }
  }
  return { primary: textDateToIso(d ?? null), other: null }
}

// d. Child sexual abuse (s.11ZA): the "no time limit" confirmation is
// OFFERED only for sexual abuse where the DOB shows a child at the accident
// date (with two dates of birth on file: BOTH must); it APPLIES only while
// that's still true and someone has confirmed.
export const abuseConfirmOffered = (accidentRaw: string | null | undefined, dobRaw: DobInput, claim?: LimitationClaimFacts | null): boolean => {
  if (claim?.claimType !== 'ABUSE' || claim?.abuseType !== 'SEXUAL') return false
  const accident = textDateToIso(accidentRaw ?? null)
  const { primary, other } = dobPair(dobRaw)
  return !!accident && childOn(primary, accident) && (!other || childOn(other, accident))
}
export const noTimeLimitApplies = (accidentRaw: string | null | undefined, dobRaw: DobInput, claim?: LimitationClaimFacts | null): boolean =>
  !!claim?.abuseNoLimitConfirmedAt && abuseConfirmOffered(accidentRaw, dobRaw, claim)

// e. Military + overseas operation: the 6-year longstop from the accident, or
// the date of knowledge if later. null = not applicable / no dates.
export const militaryLongstop = (accidentRaw: string | null | undefined, claim?: LimitationClaimFacts | null): { ymd: string; from: string } | null => {
  if (claim?.claimType !== 'MILITARY' || claim?.overseasOperation !== true) return null
  const accident = textDateToIso(accidentRaw ?? null)
  const dok = textDateToIso(claim?.dateOfKnowledge ?? null)
  const fromDok = !!dok && (!accident || dok > accident)
  const start = fromDok ? dok : accident
  const ymd = start ? addYearsIso(start, 6) : null
  return ymd ? { ymd, from: fromDok ? 'date of knowledge' : 'accident' } : null
}
export const ownDateOverLongstop = (ymd: string): string => `Can’t be later than the overseas operations longstop (${ukDate(ymd)}).`

// ── A saved Limitation deadline LATER than Casevo's estimate (B2b fix) ──
// The dangerous direction: the case's real deadline runs past the
// conservative date Casevo works out from its facts (ignoring the deadline
// itself and the own date). Shown at the TOP of Checks, as an amber dot on the
// hero / quick view, and listed in the daily nudge. Not when the deadline is
// on or before the estimate, nor while a "no time limit" confirmation (s.11ZA)
// validly applies. A later date can be right (date of knowledge, a standstill
// …) — it's a prompt to check, never changed.
export const ESTIMATE_RULE: Record<LimitationBasis, string> = {
  adult: 'accident + 3 years',
  child: 'under 18 at the accident: 18th birthday + 3 years',
  noDob: 'no date of birth: accident + 3 years',
  override: 'own date',
  cica: 'CICA: incident + 2 years',
  holiday: 'holiday, air / sea, no arrival date: accident + 2 years',
  fatal: 'fatal: date of death + 3 years',
  holidayNoRegime: 'holiday, type not chosen: accident + 1 year',
  travel: 'holiday, air / sea: arrival + 2 years',
  foreignLaw: 'holiday, foreign law: accident + the foreign period',
  mib: 'untraced driver (MIB): accident + 3 years',
  cicaChild: 'CICA, reported to police before 18: before the 20th birthday',
  cicaReport: 'CICA, reported to police at 18 or over: report + 2 years',
  disease: 'disease: earliest date + 3 years',
  militaryCap: 'overseas operations longstop: 6 years',
}
// (B2c / audit fix 1, H2) THE DATE the estimate runs from. An EL_DISEASE
// claim runs from the EARLIEST of the accident date, the first symptoms, the
// diagnosis and the date of knowledge; a CLINICAL_NEGLIGENCE claim (with an
// accident date) from the earliest of the accident, first symptoms and
// diagnosis — so the disease dates can only make it EARLIER. null = the
// accident date (no earlier disease date).
const diseaseStart = (accidentIso: string | null, claim?: LimitationClaimFacts | null): { label: string; ymd: string } | null => {
  const type = claim?.claimType ?? null
  if (type !== 'EL_DISEASE' && !(type === 'CLINICAL_NEGLIGENCE' && accidentIso)) return null
  const dates = ([
    ['date of first symptoms', textDateToIso(claim?.firstSymptomsDate ?? null)],
    ['date of diagnosis', textDateToIso(claim?.diagnosisDate ?? null)],
    ['date of knowledge', type === 'EL_DISEASE' ? textDateToIso(claim?.dateOfKnowledge ?? null) : null],
  ] as [string, string | null][]).filter((x): x is [string, string] => !!x[1]).sort((a, b) => a[1].localeCompare(b[1]))
  if (dates.length === 0) return null
  if (accidentIso && dates[0][1] >= accidentIso) return null
  return { label: dates[0][0], ymd: dates[0][1] }
}
export const estimateStartOf = (accidentRaw: string | null | undefined, claim?: LimitationClaimFacts | null): string =>
  diseaseStart(textDateToIso(accidentRaw ?? null), claim)?.label ?? 'accident'
// (Follow-ups 29 Oct) A disease estimate in words, naming ONLY the dates
// that are set: "the earliest of the accident, first symptoms, diagnosis and
// date of knowledge + 3 years" (the date of knowledge counts on EL_DISEASE
// only, as in diseaseStart). One date → "the date of knowledge + 3 years".
export const diseaseWording = (accidentRaw: string | null | undefined, claim?: LimitationClaimFacts | null): string => {
  const knowledge = claim?.claimType === 'EL_DISEASE' ? textDateToIso(claim?.dateOfKnowledge ?? null) : null
  const set = ([
    ['accident', 'accident date', textDateToIso(accidentRaw ?? null)],
    ['first symptoms', 'date of first symptoms', textDateToIso(claim?.firstSymptomsDate ?? null)],
    ['diagnosis', 'date of diagnosis', textDateToIso(claim?.diagnosisDate ?? null)],
    ['date of knowledge', 'date of knowledge', knowledge],
  ] as [string, string, string | null][]).filter((x) => !!x[2])
  if (set.length === 0) return 'the earliest disease date + 3 years'
  if (set.length === 1) return `the ${set[0][1]} + 3 years`
  const names = set.map((x) => x[0])
  return `the earliest of the ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} + 3 years`
}
// The rule in words, naming the start date (a child: "under 18 at the date of
// diagnosis: …" when that's what was used).
export const estimateRuleText = (basis: LimitationBasis, start = 'accident'): string =>
  basis === 'child' && start !== 'accident' ? `under 18 at the ${start}: 18th birthday + 3 years` : ESTIMATE_RULE[basis]
export interface LaterThanEstimate { estimateYmd: string; rule: string; message: string }
export const laterThanEstimateMessage = (estimateYmd: string, rule: string): string =>
  `This deadline is later than Casevo’s estimate (${ukDate(estimateYmd)}, ${rule}). Check it’s right.`
export const deadlineLaterThanEstimate = (
  deadlineRaw: string | null | undefined,
  accidentRaw: string | null | undefined,
  dobRaw: DobInput,
  claim?: LimitationClaimFacts | null,
): LaterThanEstimate | null => {
  const due = textDateToIso(deadlineRaw ?? null)
  if (!due) return null
  const facts = { ...(claim ?? {}), overrideDate: null }
  if (noTimeLimitApplies(accidentRaw, dobRaw, facts)) return null
  const est = estimatedLimitation(accidentRaw, dobRaw, facts)
  if (!est || due <= est.ymd) return null
  const rule = estimateRuleText(est.basis, estimateStartOf(accidentRaw, facts))
  return { estimateYmd: est.ymd, rule, message: laterThanEstimateMessage(est.ymd, rule) }
}

// CORE RULE (audit fix 1): the estimate is never LATER than the cautious date
// for the facts unless a person decided it — an own date, or a valid "no time
// limit" confirmation. A fact that legitimately moves it later (a date of
// death, an arrival date, a foreign period, the CICA child rules) carries a
// "Without it: [date]" note (test/limitationProperty.test.ts).
export const estimatedLimitation = (
  accidentRaw: string | null | undefined,
  dobRaw: DobInput,
  claim?: LimitationClaimFacts | null,
): LimitationEstimate | null => {
  // d. Confirmed "no time limit" → no estimate at all (an own date still wins).
  if (!textDateToIso(claim?.overrideDate ?? null) && noTimeLimitApplies(accidentRaw, dobRaw, claim)) return null
  // H3. Two dates of birth on file → the one giving the EARLIER date, noted.
  const { primary, other } = dobPair(dobRaw)
  let est = ruleEstimate(accidentRaw, primary, claim)
  if (!est) return null
  if (other && est.basis !== 'override') {
    const alt = ruleEstimate(accidentRaw, other, claim)
    if (alt && alt.ymd < est.ymd) est = alt
    // The same date either way → the one whose "Without it" date is earlier
    // (the cautious date without the fact that moved it).
    else if (alt && alt.ymd === est.ymd && withoutYmd(alt) < withoutYmd(est)) est = alt
    est.notes.unshift(LIMITATION_NOTES.twoDobs(primary as string, other))
  }
  // d. A "no time limit" confirmation that no longer fits (checked NOW — the
  // abuse type, claim type or dates changed, e.g. the DOB on the client page)
  // counts as unconfirmed, and says so (not on an own date: it wins anyway).
  if (claim?.abuseNoLimitConfirmedAt && est.basis !== 'override') est.notes.push(LIMITATION_NOTES.abuseConfirmStale)
  // e. The overseas operations longstop: never later than it — not even an
  // own date — and always shown.
  const cap = militaryLongstop(accidentRaw, claim)
  if (cap) {
    if (est.ymd > cap.ymd) est = { ymd: cap.ymd, basis: 'militaryCap', notes: est.basis === 'override' ? [LIMITATION_NOTES.ownDateOverCap] : est.notes }
    est.notes.push(LIMITATION_NOTES.militaryCap(cap.ymd, cap.from))
  }
  // g. A protected party: the date stays the earlier one, with a note (not
  // on the user's own date — they've already set it).
  if (est.basis !== 'override' && claim?.protectedParty === true) est.notes.push(LIMITATION_NOTES.capacity)
  return est
}
const ruleEstimate = (
  accidentRaw: string | null | undefined,
  dob: string | null,
  claim?: LimitationClaimFacts | null,
): LimitationEstimate | null => {
  // a. The user's own date wins (its reason + who set it are shown by the page).
  const override = textDateToIso(claim?.overrideDate ?? null)
  if (override) return { ymd: override, basis: 'override', notes: [] }
  const type = claim?.claimType ?? null
  const dok = textDateToIso(claim?.dateOfKnowledge ?? null)
  const accidentIso = textDateToIso(accidentRaw ?? null)
  // f / H2. Disease (and clinical negligence) claims run from the EARLIEST of
  // the accident and the disease dates — not only when there's no accident date.
  const ds = diseaseStart(accidentIso, claim)
  const diseaseFrom: string | null = ds ? ds.label : null
  const accident = ds ? ds.ymd : accidentIso
  if (!accident) return null
  const child = childOn(dob, accident)
  const eighteenth = child && dob ? addYearsIso(dob, 18) : null
  // b2. Untraced driver (MIB) — road traffic only (H1): 3 years, NO child
  // extension (never later than the base rule).
  if (claim?.claimRoute === 'MIB_UNTRACED' && type === 'RTA') {
    const ymd = addYearsIso(accident, 3)
    return ymd ? { ymd, basis: 'mib', notes: [LIMITATION_NOTES.mib] } : null
  }
  // c. CICA (not the general child rule): an adult → incident + 2 years; a
  // child → by the police report — before their 18th birthday: before their
  // 20th birthday (the day before it); on/after it: report + 2 years; no
  // report date yet → incident + 2 years, asking for it. A child rule giving
  // a LATER date says what it'd be without it (incident + 2 years). A report
  // date before the incident (a typo) isn't used.
  if (type === 'CICA') {
    const twoYears = addYearsIso(accident, 2)
    if (!twoYears) return null
    if (!child || !dob || !eighteenth) return { ymd: twoYears, basis: 'cica', notes: [] }
    const report = textDateToIso(claim?.policeFirstReportedAt ?? null)
    if (!report) return { ymd: twoYears, basis: 'cica', notes: [LIMITATION_NOTES.cicaNoReport] }
    if (report < accident) return { ymd: twoYears, basis: 'cica', notes: [LIMITATION_NOTES.reportBeforeIncident] }
    const later = (ymd: string, basis: LimitationBasis, note: string): LimitationEstimate =>
      ({ ymd, basis, notes: ymd > twoYears ? [note, LIMITATION_NOTES.withoutIt(basis === 'cicaChild' ? 'CICA rule for children' : 'date first reported to police', twoYears)] : [note] })
    if (report < eighteenth) {
      const twentieth = addYearsIso(dob, 20)
      return twentieth ? later(addDaysIso(twentieth, -1), 'cicaChild', LIMITATION_NOTES.cicaChildBefore18) : null
    }
    const ymd = addYearsIso(report, 2)
    return ymd ? later(ymd, 'cicaReport', LIMITATION_NOTES.cicaChildFrom18) : null
  }
  // a'. Holiday / foreign, by regime. No regime → 1 year (the earliest).
  const notes: string[] = []
  if (type === 'HOLIDAY_FOREIGN') {
    const regime = claim?.holidayRegime ?? null
    if (regime === 'AIR' || regime === 'SEA') {
      const typed = textDateToIso(claim?.travelArrivalDate ?? null)
      // An arrival before the accident (a typo) isn't used.
      const arrival = typed && typed >= accident ? typed : null
      const ymd = addYearsIso(arrival ?? accident, 2)
      const standard = addYearsIso(accident, 2)
      if (!ymd || !standard) return null
      const n = [LIMITATION_NOTES.holidayTravel]
      if (!typed) n.push(LIMITATION_NOTES.holidayNoArrival)
      else if (!arrival) n.push(LIMITATION_NOTES.arrivalBeforeAccident)
      if (ymd > standard) n.push(LIMITATION_NOTES.withoutIt('arrival date', standard))
      return { ymd, basis: arrival ? 'travel' : 'holiday', notes: n }
    }
    if (regime === 'FOREIGN_LAW') {
      const months = validMonths(claim?.foreignLimitationMonths)
      const ymd = months ? addMonthsIso(accident, months) : addYearsIso(accident, 1)
      const standard = addYearsIso(accident, 1)
      if (!ymd || !standard) return null
      const n = [months ? LIMITATION_NOTES.foreignLaw(months) : LIMITATION_NOTES.foreignLawNoMonths]
      if (ymd > standard) n.push(LIMITATION_NOTES.withoutIt('foreign limitation period entered', standard))
      return { ymd, basis: 'foreignLaw', notes: n }
    }
    if (regime !== 'PACKAGE') {
      const ymd = addYearsIso(accident, 1)
      return ymd ? { ymd, basis: 'holidayNoRegime', notes: [LIMITATION_NOTES.holidayNoRegime] } : null
    }
    // PACKAGE → the usual rule below (the child rule applies).
    notes.push(LIMITATION_NOTES.holidayPackage, LIMITATION_NOTES.holidayPackageTravel)
  }
  // b. Base: a child at the accident → 3 years after their 18th birthday;
  // otherwise accident + 3 years.
  const base = child ? (eighteenth ? addYearsIso(eighteenth, 3) : null) : addYearsIso(accident, 3)
  if (!base) return null
  const basis: LimitationBasis = child ? 'child' : diseaseFrom ? 'disease' : dob ? 'adult' : 'noDob'
  // e. Fatal: death BEFORE the base date → 3 years from death; death on or
  // after it (already expired) → keep the base date, noted.
  const death = textDateToIso(claim?.dateOfDeath ?? null)
  if (type === 'FATAL') {
    if (death && death < base) {
      const ymd = addYearsIso(death, 3)
      // Later than the base date → say what it would be without the death
      // date (a mistyped date of death must never quietly add years).
      if (ymd) return { ymd, basis: 'fatal', notes: ymd > base ? [LIMITATION_NOTES.fatalLater(base)] : [] }
    }
    if (death && death >= base) notes.push(LIMITATION_NOTES.deathAfterExpiry)
    if (!death) notes.push(LIMITATION_NOTES.fatalNoDeath)
  } else if (death && (type === 'EL_DISEASE' || type === 'CLINICAL_NEGLIGENCE' || type === 'CATASTROPHIC')) {
    // (Audit fix 1) A date of death on these types never moves the date.
    notes.push(LIMITATION_NOTES.diedNotFatal)
  }
  // h. Disease / clinical negligence usually run from the date of knowledge
  // — the estimate stays on the earliest date (conservative), with a note
  // naming the date it's based on.
  if (diseaseFrom) notes.push(accidentIso ? LIMITATION_NOTES.diseaseBeforeAccident(diseaseFrom) : LIMITATION_NOTES.diseaseBase(diseaseFrom))
  if (!(diseaseFrom && !accidentIso) && (type === 'EL_DISEASE' || type === 'CLINICAL_NEGLIGENCE')) notes.push(LIMITATION_NOTES.knowledgeClaim)
  // d. Abuse: sexual (or not yet said) and not shown to be an adult, and not
  // confirmed → may have no time limit. (A confirmation that no longer fits
  // is flagged by estimatedLimitation.)
  if (type === 'ABUSE' && !claim?.abuseNoLimitConfirmedAt && (claim?.abuseType === 'SEXUAL' || !claim?.abuseType) && !(dob && !child)) {
    notes.push(LIMITATION_NOTES.abuseMaybeNoLimit)
  }
  // f. A LATER date of knowledge never moves the estimate — it's noted with
  // the date it would give (a child: from the later of their 18th birthday
  // and the date of knowledge).
  if (dok && dok > accident) {
    const from = eighteenth && eighteenth > dok ? eighteenth : dok
    const byDok = addYearsIso(from, 3)
    if (byDok && byDok > base) notes.push(LIMITATION_NOTES.dateOfKnowledge(byDok))
  }
  return { ymd: base, basis, notes }
}

// The badge beside an estimate, and how it was worked out.
export const estimateBadge = (basis: LimitationBasis): string =>
  basis === 'noDob' ? 'est. (no date of birth)' : basis === 'override' ? 'own date' : 'est.'
// In running text (the quick view, the PDF export): "(est.)" / "(est., no date of birth)".
export const estimateParen = (basis: LimitationBasis): string =>
  basis === 'noDob' ? '(est., no date of birth)' : basis === 'override' ? '(own date)' : '(est.)'
export const estimateBasisText = (basis: LimitationBasis): string => ({
  adult: 'accident + 3y',
  child: '18th birthday + 3y',
  noDob: 'accident + 3y · no date of birth',
  override: 'own date',
  cica: 'incident + 2y',
  holiday: 'accident + 2y',
  fatal: 'date of death + 3y',
  holidayNoRegime: 'accident + 1y',
  travel: 'arrival + 2y',
  foreignLaw: 'foreign law period',
  mib: 'accident + 3y (MIB)',
  cicaChild: 'before 20th birthday',
  cicaReport: 'police report + 2y',
  disease: 'earliest disease date + 3y',
  militaryCap: 'overseas operations longstop',
} as const)[basis]

// (B2c) The child explanation in the limitation box / Overview, naming the
// date actually used ("at the date of diagnosis" when there's no accident date).
export const childWording = (start = 'accident'): string =>
  `3 years after the client’s 18th birthday (they were under 18 at the ${start})`
// What to add for an estimate when there's none: a disease claim can use its
// own dates instead of an accident date.
export const NO_ESTIMATE_ADD = 'Add an accident date for an estimate'
export const NO_ESTIMATE_ADD_DISEASE = 'Add an accident date, or the first symptoms / diagnosis / date of knowledge (Case control → Claim), for an estimate'
export const NEEDS_DATE_SHORT = 'Needs an accident date'
export const NEEDS_DATE_SHORT_DISEASE = 'Needs an accident or disease date'
export const noEstimateHint = (claimType: string | null | undefined, short = false): string =>
  claimType === 'EL_DISEASE' ? (short ? NEEDS_DATE_SHORT_DISEASE : NO_ESTIMATE_ADD_DISEASE) : (short ? NEEDS_DATE_SHORT : NO_ESTIMATE_ADD)

export type LimitationCheck = { kind: 'error' | 'warn'; message: string } | null

// (B2b fix 2) A limitation date (the saved deadline, or the estimate / own
// date when none is saved) that is BEFORE today — red in the limitation box,
// the Overview's key dates and the hero. A completed deadline isn't flagged.
export const LIMITATION_PASSED = 'This date has passed'
export const limitationDatePassed = (ymd: string | null | undefined, today: string): boolean => !!ymd && ymd.slice(0, 10) < today

// The check on a limitation deadline's date (the deadline edit card, the
// quick view, the limitation box):
//   • before the accident date → error (the server blocks it too);
//   • LATER than Casevo's estimate → the amber deadlineLaterThanEstimate
//     message (B2b fix — replaces the old "Not 3 years after the accident" /
//     "Client was under 18…" warnings, so there's one consistent message,
//     and it now knows every rule: CICA, holiday, MIB, military, disease…).
// A date on or before the estimate is the safe direction — no warning.
export const limitationDateCheck = (
  dueRaw: string | null | undefined,
  accidentRaw: string | null | undefined,
  dobRaw: DobInput,
  claim?: LimitationClaimFacts | null,
): LimitationCheck => {
  const due = textDateToIso(dueRaw ?? null)
  if (!due) return null
  const accident = textDateToIso(accidentRaw ?? null)
  if (accident && due < accident) return { kind: 'error', message: `A limitation date can’t be before the accident date (${ukDate(accident)}).` }
  const later = deadlineLaterThanEstimate(due, accidentRaw, dobRaw, claim)
  return later ? { kind: 'warn', message: later.message } : null
}
